import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import Tooltip from '@mui/material/Tooltip';
import { Icon } from '../theme/Icon';
import { ToastContext } from './toastContext';
import {
  WAIT_TICKMS,
  WaitContext,
  type ActionSlowAPI,
  type WaitOptions,
  type TaskInExecution,
} from './waitContext';
import s from './TarefasEmExecucao.module.css';
import { locale, t, tn } from '@app/i18n';

/**
 * Título padrão de uma tarefa sem `titulo`. Função, não const: uma const de
 * módulo com `t()` congelaria o idioma da primeira importação (qa-i18n, 09/09).
 */
function titleDefault(): string {
  return t('common.ui.tasks.defaultTitle');
}

/** `7000` → `00:07`. */
function clk(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const minutes = String(Math.floor(total / 60)).padStart(2, '0');
  const seconds = String(total % 60).padStart(2, '0');
  return `${minutes}:${seconds}`;
}

/** `7420` → `7,4 s`; `15200` → `15 s`. Décimo só enquanto ele significa algo. */
function duration(ms: number): string {
  const seguros = Math.max(0, ms);
  if (seguros >= 10_000) return t('common.ui.tasks.duration', { n: Math.round(seguros / 1000) });
  const decimos = Math.round(seguros / 100) / 10;
  // Uma casa decimal sempre («7,0 s»), no separador do idioma ativo — o
  // `replace('.', ',')` de antes era pt-BR fixo.
  const fmt = new Intl.NumberFormat(locale(), {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
  return t('common.ui.tasks.duration', { n: fmt.format(decimos) });
}

/** `120000` → `2 minutos`; `90000` → `1 minuto e meio`; `45000` → `45 segundos`. */
function limit(ms: number): string {
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) return tn('common.ui.tasks.seconds', seconds);
  const minutes = seconds / 60;
  if (minutes === 1.5) return t('common.ui.tasks.minuteAndHalf');
  return tn('common.ui.tasks.minutes', Math.round(minutes));
}

export interface TasksInExecutionProps {
  /** Versão reduzida, para caber numa linha de card (`ConnectionCard`). */
  compacto?: boolean;
  className?: string;
}

/**
 * Indicador **não bloqueante** das ações demoradas em voo (29/08).
 *
 * Substitui o modal de espera: o mrolim reprovou travar a tela no lab. O que
 * sobra é um spinner com a contagem no cabeçalho — a tela inteira continua
 * utilizável, e só o botão que disparou a ordem fica desabilitado (isso é do
 * `LoadingButton` de cada tela, pelo `isPending` da mutação).
 *
 * Sem tarefa nenhuma o componente **não renderiza** — nem uma caixa vazia.
 *
 * O relógio de cada tarefa mora no tooltip do ⓘ, não no rótulo: assim o
 * `role="status"` anuncia «Tarefas em execução (1)» uma vez, em vez de recitar
 * «um», «dois», «três» a cada segundo para quem usa leitor de tela.
 */
export function TasksInExecution({ compacto = false, className }: TasksInExecutionProps) {
  const { tarefas } = useActionOptionalSlow() ?? { tarefas: [] as TaskInExecution[] };
  const [agora, setAgora] = useState(() => Date.now());
  const howMany = tarefas.length;

  // O tique só corre enquanto há o que contar. `setAgora` na entrada evita o
  // primeiro segundo tirado de um `Date.now()` velho.
  useEffect(() => {
    if (howMany === 0) return;
    setAgora(Date.now());
    const id = window.setInterval(() => setAgora(Date.now()), WAIT_TICKMS);
    return () => window.clearInterval(id);
  }, [howMany]);

  if (howMany === 0) return null;

  return (
    <span
      className={[s.indicador, compacto ? s.compacto : '', className].filter(Boolean).join(' ')}
      data-testid="tarefas-em-execucao"
      data-tarefas={howMany}
      role="status"
      aria-live="polite"
    >
      <span className={s.spinner} aria-hidden="true" />
      <span className={s.rotulo}>{t('common.ui.tasks.count', { n: howMany })}</span>
      <Tooltip
        title={
          <ul className={s.lista} data-testid="tarefas-detalhe">
            {tarefas.map((tarefa) => (
              <li key={tarefa.id}>
                {tarefa.titulo} · <span className={s.tempo}>{clk(agora - tarefa.inicio)}</span>
                {tarefa.limiteMs ? (
                  <span className={s.teto}>
                    {' '}
                    {t('common.ui.tasks.until', { limit: limit(tarefa.limiteMs) })}
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        }
        arrow
        enterDelay={0}
        enterTouchDelay={0}
        describeChild
      >
        <button type="button" className={s.tip} aria-label={t('common.ui.tasksDetail')}>
          <Icon name="info" size={compacto ? 'xs' : 'sm'} />
        </button>
      </Tooltip>
    </span>
  );
}

/** Como `useAcaoDemorada`, mas sem explodir fora do provedor. */
function useActionOptionalSlow(): ActionSlowAPI | null {
  return useContext(WaitContext);
}

export interface WaitProviderProps {
  children: ReactNode;
}

/**
 * Registro das ações demoradas da árvore, como o `ToastProvider` faz com o
 * aviso. Mora no `App` — e nos helpers de teste, senão `useAcaoDemorada` explode
 * em quem renderiza a tela isolada.
 *
 * ```tsx
 * const { executar } = useAcaoDemorada();
 * await executar(() => discovery.mutateAsync(), { titulo: 'Discovery' });
 * ```
 *
 * Quem mostra o registro é o `<TarefasEmExecucao />` do cabeçalho — o provedor
 * não desenha nada. Duas ordens ao mesmo tempo são duas tarefas; nenhuma
 * cancela a outra.
 */
export function WaitProvider({ children }: WaitProviderProps) {
  const [tarefas, setTarefas] = useState<readonly TaskInExecution[]>([]);
  const seqRef = useRef(0);

  // O toast é opcional de propósito: `EsperaProvider` também é montado sozinho
  // em teste, e um provedor que exige o outro só troca um erro por outro.
  const toast = useContext(ToastContext);
  const toastRef = useRef(toast);
  toastRef.current = toast;

  const execute = useCallback(async function execute<T>(
    fn: () => Promise<T>,
    opcoes?: WaitOptions,
  ): Promise<T> {
    seqRef.current += 1;
    const id = seqRef.current;
    const start = Date.now();
    const titulo = opcoes?.titulo ?? titleDefault();
    setTarefas((atuais) => [
      ...atuais,
      { id, titulo, inicio: start, limiteMs: opcoes?.limiteMs ?? null, chave: null },
    ]);

    try {
      const output = await fn();
      // Fecho da ação, já que não há mais modal dizendo «Concluído em …». Quem
      // tem toast próprio (as contagens do Discovery, «PDP republicado») o
      // mostra logo depois e substitui este — a mensagem mais específica vence,
      // e é a que o operador quer ver.
      toastRef.current?.mostrar(
        t('common.ui.tasks.done', { title: titulo, duration: duration(Date.now() - start) }),
        {
          tone: 'sucesso',
        },
      );
      return output;
    } finally {
      // Sucesso ou falha, a tarefa sai do indicador. O erro segue subindo: o
      // banner da tela é que mostra o código (FR-123).
      setTarefas((atuais) => atuais.filter((tarefa) => tarefa.id !== id));
    }
  }, []);

  // `manter`/`soltar`: a tarefa que dura no **servidor** e não numa promessa daqui
  // (o discovery desde 29/08). Idempotente por chave — ver a doc em `esperaContext`.
  const keep = useCallback((chave: string, opcoes?: WaitOptions) => {
    setTarefas((atuais) => {
      if (atuais.some((tarefa) => tarefa.chave === chave)) return atuais;
      seqRef.current += 1;
      return [
        ...atuais,
        {
          id: seqRef.current,
          titulo: opcoes?.titulo ?? titleDefault(),
          inicio: Date.now(),
          limiteMs: opcoes?.limiteMs ?? null,
          chave,
        },
      ];
    });
  }, []);

  const soltar = useCallback((chave: string) => {
    setTarefas((atuais) => {
      const remaining = atuais.filter((tarefa) => tarefa.chave !== chave);
      // Mesma referência quando nada saiu: um array novo a cada polling faria o
      // `useMemo` abaixo render a árvore inteira de 3 em 3 segundos.
      return remaining.length === atuais.length ? atuais : remaining;
    });
  }, []);

  const api = useMemo<ActionSlowAPI>(
    () => ({ executar: execute, manter: keep, soltar, tarefas }),
    [execute, keep, soltar, tarefas],
  );

  return <WaitContext.Provider value={api}>{children}</WaitContext.Provider>;
}
